import { capitalize, Divider, Paper, Typography } from "@mui/material";
import {
  CarbonMeasurementSystem,
  CarbonUnit,
  UnitSelector,
} from "common/utils/units/units";
import { DimensionedNumberDisplay } from "common/components/DimensionedNumberDisplay";
import { ErrorMessage, useFormikContext } from "formik";
import {
  hitchTypeToLocaleString,
  ptoSpeedToLocaleString,
} from "common/utils/enumTranslations";
import { StepProps } from "./StepProps";
import { TractorViewModel } from "../TractorViewModel";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, ReactNode } from "react";

export const ReviewStep: FC<StepProps> = () => {
  const { values } = useFormikContext<TractorViewModel>();
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        <InfoOutlinedIcon className="flex my-auto" />
        <Typography>
          {t("views.autotractor.equipment.atkSetupReminderTractor")}
        </Typography>
      </div>
      <Paper className="flex flex-col gap-2 p-4">
        <FieldPreview
          label={t("utils.descriptors.name")}
          name="name"
          value={values.name}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.make_one"))}
          name="archetype.make"
          archetypeValue={values.archetype?.make}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.model_one"))}
          name="archetype.model"
          archetypeValue={values.archetype?.model}
        />
        <Typography className="font-bold">
          {capitalize(
            t("views.autotractor.equipment.tractors.physicalDimensions")
          )}
        </Typography>
        <FieldPreview
          label={capitalize(
            t("views.autotractor.equipment.tractors.frontmostPointToFrontAxle")
          )}
          value={
            values.driveInfo.boundaryViewModel.measuringTapeV1
              ?.yNoseToFrontAxleM
          }
          archetypeValue={0} // the value will always be defined, so this is not used
          name="noseToFrontAxleM"
          dimensionInfo={{
            storedUnit: "m",
            displayUnits: { imperial: "in", metric: "cm" },
            localSystem: measurementSystem,
          }}
        />
        <FieldPreview
          label={capitalize(t("models.equipment.wheelSpacing"))}
          value={values.driveInfo.wheelSpacingM}
          archetypeValue={values.archetype?.wheelSpacingM}
          name="archetype.wheelSpacingM"
          dimensionInfo={{
            storedUnit: "m",
            displayUnits: { imperial: "in", metric: "cm" },
            localSystem: measurementSystem,
          }}
        />
        <FieldPreview
          label={capitalize(
            t("views.autotractor.equipment.tractors.overallLength")
          )}
          value={
            values.driveInfo.boundaryViewModel.measuringTapeV1?.overallLengthM
          }
          archetypeValue={0} // the value will always be defined, so this is not used
          name="overallLengthM"
          dimensionInfo={{
            storedUnit: "m",
            displayUnits: { imperial: "in", metric: "cm" },
            localSystem: measurementSystem,
          }}
        />
        <FieldPreview
          label={capitalize(
            t("views.autotractor.equipment.tractors.overallWidth")
          )}
          value={
            values.driveInfo.boundaryViewModel.measuringTapeV1?.overallWidthM
          }
          archetypeValue={0} // the value will always be defined, so this is not used
          name="overallWidthM"
          dimensionInfo={{
            storedUnit: "m",
            displayUnits: { imperial: "in", metric: "cm" },
            localSystem: measurementSystem,
          }}
        />
        <Typography className="font-bold">
          {capitalize(t("views.autotractor.equipment.tractors.driveInfo"))}
        </Typography>
        <FieldPreview
          label={capitalize(t("models.equipment.turningRadius"))}
          value={values.driveInfo.minTurningRadiusM}
          archetypeValue={0} // the value will always be defined, so this is not used
          name="minTurningRadiusM"
          dimensionInfo={{
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
          }}
        />
        <div className="flex flex-col gap-2 pl-4">
          <Typography className="font-bold">
            {capitalize(t("models.equipment.steeringRange"))}
          </Typography>
          <FieldPreview
            label={capitalize(t("utils.descriptors.minimum"))}
            value={values.driveInfo.steeringRange.min}
            archetypeValue={values.archetype?.steeringRange?.min}
            name="steeringRange.min"
            dimensionInfo={{
              storedUnit: "deg",
              displayUnits: { imperial: "deg", metric: "deg" },
              localSystem: measurementSystem,
            }}
          />
          <FieldPreview
            label={capitalize(t("utils.descriptors.maximum"))}
            value={values.driveInfo.steeringRange.max}
            name="steeringRange.max"
            archetypeValue={values.archetype?.steeringRange?.max}
            dimensionInfo={{
              storedUnit: "deg",
              displayUnits: { imperial: "deg", metric: "deg" },
              localSystem: measurementSystem,
            }}
          />
        </div>
        <Typography className="font-bold">
          {capitalize(t("models.equipment.hitch_other"))}
        </Typography>
        <div className="pl-4 flex flex-col gap-2">
          {values.hitches &&
            values.hitches.hitches.map((h, i) => (
              <div key={`hitch-${i}`}>
                <Typography>{t(hitchTypeToLocaleString(h.type))}</Typography>
                <div className="pl-2">
                  <FieldPreview
                    label={capitalize(
                      t("models.equipment.offsetFromFrontAxle")
                    )}
                    name={`hitches.hitches[${i}].offsetYM`}
                    value={h.offsetYM}
                    dimensionInfo={{
                      storedUnit: "m",
                      displayUnits: { imperial: "in", metric: "cm" },
                      localSystem: measurementSystem,
                    }}
                  />
                </div>
              </div>
            ))}
          {!values.hitches &&
            values.archetype?.hitches?.hitches.map((h, i) => (
              <div key={`hitch-${i}`}>
                <Typography>{t(hitchTypeToLocaleString(h.type))}</Typography>
                <div className="pl-2">
                  <FieldPreview
                    label={capitalize(
                      t("models.equipment.offsetFromFrontAxle")
                    )}
                    name={`archetype.hitches.hitches[${i}].offsetYM`}
                    value={h.offsetYM}
                    dimensionInfo={{
                      storedUnit: "m",
                      displayUnits: { imperial: "in", metric: "cm" },
                      localSystem: measurementSystem,
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
        <Typography className="font-bold">
          {capitalize(t("models.equipment.pto_short"))}
        </Typography>
        <div className="pl-4 flex flex-col">
          <Typography>{capitalize(t("utils.descriptors.front"))}</Typography>
          <div className="pl-2">
            <FieldPreview
              label={capitalize(t("utils.descriptors.speed_other"))}
              name="frontPto.availableSpeeds"
              value={values.frontPto?.availableSpeeds
                .map((s) => t(ptoSpeedToLocaleString(s)))
                .join(", ")}
              archetypeValue={values.archetype?.frontPto?.availableSpeeds
                .map((s) => t(ptoSpeedToLocaleString(s)))
                .join(", ")}
            />
          </div>
          <Typography>{capitalize(t("utils.descriptors.rear"))}</Typography>
          <div className="pl-2">
            <FieldPreview
              label={capitalize(t("utils.descriptors.speed_other"))}
              name="rearPto.availableSpeeds"
              value={values.rearPto?.availableSpeeds
                .map((s) => t(ptoSpeedToLocaleString(s)))
                .join(", ")}
              archetypeValue={values.archetype?.rearPto?.availableSpeeds
                .map((s) => t(ptoSpeedToLocaleString(s)))
                .join(", ")}
            />
          </div>
        </div>
        <Typography className="font-bold">
          {capitalize(t("models.equipment.scv_short", { count: 1 }))}
        </Typography>
        <div className="pl-4 flex flex-col gap-4">
          {values.scvOutputs &&
            values.scvOutputs.scvOutput.map((o, i) => (
              <div key={`scv-${i}`} className="flex flex-col gap-2">
                <FieldPreview
                  label={t("models.equipment.port")}
                  name={`scvOutputs.scvOutput[${i}].port`}
                  value={o.port}
                />
                <div className="pl-2 flex flex-col gap-2">
                  <FieldPreview
                    label={capitalize(t("utils.descriptors.description_one"))}
                    name={`scvOutputs.scvOutput[${i}].description`}
                    value={o.description}
                  />
                  <FieldPreview
                    label={t("utils.descriptors.disabled")}
                    name={`scvOutputs.scvOutput[${i}].disabled`}
                    value={o.disabled}
                  />
                  <FieldPreview
                    label={capitalize(t("models.equipment.couplerDiameter"))}
                    name={`scvOutputs.scvOutput[${i}].couplerDiameter`}
                    value={o.couplerDiameterMm}
                    dimensionInfo={{
                      storedUnit: "mm",
                      displayUnits: { imperial: "in", metric: "mm" },
                      localSystem: measurementSystem,
                    }}
                  />
                </div>
              </div>
            ))}
          {!values.scvOutputs &&
            values.archetype?.scvOutputs?.scvOutput.map((o, i) => (
              <div key={`scv-${i}`} className="flex flex-col gap-2">
                <FieldPreview
                  label={capitalize(t("models.equipment.port"))}
                  name={`archetype.scvOutputs.scvOutput[${i}].port`}
                  value={o.port}
                />
                <div className="pl-2 flex flex-col gap-2">
                  <FieldPreview
                    label={capitalize(t("utils.descriptors.description_one"))}
                    name={`archetype.scvOutputs.scvOutput[${i}].description`}
                    value={o.description}
                  />
                  <FieldPreview
                    label={t("utils.descriptors.disabled")}
                    name={`archetype.scvOutputs.scvOutput[${i}].disabled`}
                    value={o.disabled}
                  />
                  <FieldPreview
                    label={capitalize(t("models.equipment.couplerDiameter"))}
                    name={`archetype.scvOutputs.scvOutput[${i}].couplerDiameter`}
                    value={o.couplerDiameterMm}
                    dimensionInfo={{
                      storedUnit: "mm",
                      displayUnits: { imperial: "in", metric: "mm" },
                      localSystem: measurementSystem,
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </Paper>
    </div>
  );
};

interface FieldPreviewProps {
  label: string;
  value?: string | number | boolean;
  dimensionInfo?: {
    storedUnit: CarbonUnit;
    displayUnits: UnitSelector;
    localSystem: CarbonMeasurementSystem;
    roundToNPlaces?: number;
  };
  name: string;
  archetypeValue?: string | number | boolean;
}
export const FieldPreview: FC<FieldPreviewProps> = ({
  label,
  value,
  dimensionInfo,
  name,
  archetypeValue,
}): ReactNode => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <Typography>{label}</Typography>
        <div className="flex gap-2">
          <ErrorMessage
            name={name}
            render={(e) => {
              return <Typography className="text-error-500">{t(e)}</Typography>;
            }}
          />
          {dimensionInfo && (
            <>
              {value === undefined && (
                <DimensionedNumberDisplay
                  roundDisplayToNPlaces={dimensionInfo.roundToNPlaces ?? 1}
                  storedValue={archetypeValue as number}
                  storedUnit={dimensionInfo.storedUnit}
                  displayUnits={dimensionInfo.displayUnits}
                  localSystem={dimensionInfo.localSystem}
                />
              )}
              {value !== undefined && (
                <DimensionedNumberDisplay
                  roundDisplayToNPlaces={dimensionInfo.roundToNPlaces ?? 1}
                  storedValue={value as number}
                  storedUnit={dimensionInfo.storedUnit}
                  displayUnits={dimensionInfo.displayUnits}
                  localSystem={dimensionInfo.localSystem}
                />
              )}
            </>
          )}
          {!dimensionInfo && (
            <>
              {value === undefined && <Typography>{archetypeValue}</Typography>}
              {value !== undefined && <Typography>{value}</Typography>}
            </>
          )}
        </div>
      </div>
      <Divider />
    </div>
  );
};
